import { useDialog, useRenderNotifier } from '@wui/common-library'
import type { HistoryMessageItem, IMFile, MessageIncomingEvent, MessageOutgoingEvent } from '@wui/im'
import type { Component } from 'vue'

import { useLocalization } from '@/shared/composables/localization'
import { download as downloadFileByFallback } from '@/shared/utils/download-files'

import {
  isMediaAttachment,
  isRtfFile,
  resolveAttachmentContentKind,
} from './file-type-utils'

export const loadDocumentViewerComponent = async (
  contentKind: NonNullable<ReturnType<typeof resolveAttachmentContentKind>>,
): Promise<Component> => {
  if (contentKind === 'media') {
    const { default: component } = await import('../ui/viewers/image-video-viewer.vue')
    return component
  }

  switch (contentKind) {
  case 'spreadsheet': {
    const { default: component } = await import('../ui/viewers/xlsx-viewer.vue')
    return component
  }
  case 'word': {
    const { default: component } = await import('../ui/viewers/docx-viewer.vue')
    return component
  }
  case 'markdown': {
    const { default: component } = await import('../ui/viewers/md-viewer.vue')
    return component
  }
  case 'pdf': {
    const { default: component } = await import('../ui/viewers/pdf-viewer.vue')
    return component
  }
  }
}

export const useOpenAttachmentViewer = () => {
  const { showDialog } = useDialog()
  const { notifyError } = useRenderNotifier()
  const { t } = useLocalization()

  const openAttachmentViewer = async (
    file: IMFile,
    event: HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent>,
  ) => {
    if (isRtfFile(file)) {
      downloadFileByFallback(file, '/files/')
      return
    }

    const contentKind = resolveAttachmentContentKind(file)
    if (!isMediaAttachment(file) && !contentKind) {
      notifyError(`${t('common.unknownError')}. ${t('common.download')}.`)
      downloadFileByFallback(file, '/files/')
      return
    }

    const { default: AttachmentWithThreadChat } = await import('../ui/attachment-with-thread-chat.vue')
    showDialog(AttachmentWithThreadChat, {
      filecookie: file.filecookie,
      event,
      hasOverlay: true,
    })
  }

  return { openAttachmentViewer }
}
